import React, { useState, useMemo, useEffect, useRef } from 'react';
import SortBySelect from '../../dashboard-shared/sort-by-select/sort-by-select';
import LineGraph from './line-graph';
import Modal from 'react-bootstrap/Modal';
import DateRangePicker from '../../../../components/date-range-picker/date-range-picker';
import dayjs from '../../../../util/dayjs-config';
import '../active-tasks-bar-graph/active-strs-bar-chart.scss';
import '../../dashboard-shared/completed-vs-overdue.scss';
import FluentUiIcon from '../../../../assets/fluent-ui-icon.jsx';
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { formatLabels } from '../../dashboard-util/dashboard-utils.jsx';
import PropTypes from 'prop-types';
import { Dropdown, Option } from '@fluentui/react-components';

const NoFilingVsCompletedLineGraph = ({
  data,
  fetchData,
  customStart,
  customEnd,
  setCustomStart,
  setCustomEnd,
  selectedOwnerId,
  setSelectedOwnerId,
}) => {
  const [calendarShow, setCalendarShow] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [sort, setSort] = useState('Last 7 Days');

  const [localOwnerName, setLocalOwnerName] = useState(
    localStorage.getItem('noFilingVsComplete_owner_name') || 'All owners'
  );

  const prevSortRef = useRef(sort);
  const prevCustomStartRef = useRef(customStart);
  const prevCustomEndRef = useRef(customEnd);

  const owners = Array.isArray(data?.Owners) ? data.Owners : [];

  const currentOwnerId = selectedOwnerId ?? data?.CurrentOwner?.OwnerId ?? 0;
  const currentOwnerName =
    owners.find(o => o.OwnerId === currentOwnerId)?.OwnerName ||
    localOwnerName ||
    'All owners';

  useEffect(() => {
    localStorage.setItem('noFilingVsComplete_owner_id', selectedOwnerId ?? '0');
    localStorage.setItem('noFilingVsComplete_owner_name', currentOwnerName || 'All owners');
  }, [selectedOwnerId, currentOwnerName]);

  useEffect(() => {
    const storedOwnerId = localStorage.getItem('noFilingVsComplete_owner_id');
    const storedOwnerName = localStorage.getItem('noFilingVsComplete_owner_name');
    const storedSort = localStorage.getItem('noFilingVsComplete_sort') || 'Last 7 Days';
    const storedStart = localStorage.getItem('noFilingVsComplete_sort_start');
    const storedEnd = localStorage.getItem('noFilingVsComplete_sort_end');

    if (storedOwnerId && storedOwnerId !== 'undefined') {
      setSelectedOwnerId(Number(storedOwnerId) || null);
      setLocalOwnerName(storedOwnerName || 'All owners');
    }

    if (storedStart && storedEnd && storedStart !== 'undefined' && storedEnd !== 'undefined') {
      const start = new Date(storedStart);
      const end = new Date(storedEnd);
      setCustomStart(start);
      setCustomEnd(end);
      const owner = storedOwnerId && Number(storedOwnerId) > 0 ? Number(storedOwnerId) : null;
      fetchData(start, end, owner);
      setSort(storedSort);
    } else {
      handleDateRange(storedSort);
    }
  }, []);

  const selectedValue =
    owners.some(o => o.OwnerId === currentOwnerId)
      ? String(currentOwnerId)
      : '0';

  const handleOwnerChange = (_, optionData) => {
    const picked = Number(optionData.optionValue);
    const normalized = picked > 0 ? picked : null;
    const name = picked > 0
      ? owners.find(o => o.OwnerId === picked)?.OwnerName || 'All owners'
      : 'All owners';

    setSelectedOwnerId(normalized);
    setLocalOwnerName(name);

    if (customStart && customEnd) {
      fetchData(customStart, customEnd, normalized);
    }
  };

  const handleSelect = (selectedValue) => {
    if (['Last 7 Days', 'Last Month', 'Last 3 Months'].includes(selectedValue)) {
      setSort(selectedValue);
      handleDateRange(selectedValue);
    } else {
      if (sort !== 'Custom') {
        prevSortRef.current = sort;
        prevCustomStartRef.current = customStart;
        prevCustomEndRef.current = customEnd;
        setCustomStart(undefined);
        setCustomEnd(undefined);
      }
      setCalendarShow(true);
      setSort('Custom');
    }
  };

  const handleDateRange = (selectedValue) => {
    let startDate = new Date();
    let endDate = new Date();

    if (selectedValue === 'Last 7 Days') startDate.setDate(startDate.getDate() - 6);
    else if (selectedValue === 'Last Month') startDate.setDate(startDate.getDate() - 30);
    else if (selectedValue === 'Last 3 Months') startDate.setDate(startDate.getDate() - 90);
    else if (selectedValue === 'Custom') {
      startDate = customStart;
      endDate = customEnd;
    }

    localStorage.setItem('noFilingVsComplete_sort', selectedValue);
    localStorage.setItem('noFilingVsComplete_sort_start', startDate.toISOString());
    localStorage.setItem('noFilingVsComplete_sort_end', endDate.toISOString());

    const needFetch =
      selectedValue === 'Custom' ||
      !customStart || !customEnd ||
      startDate.getTime() !== customStart.getTime() ||
      endDate.getTime() !== customEnd.getTime();

    if (needFetch) {
      setCustomStart(startDate);
      setCustomEnd(endDate);

      clearTimeout(window.noFilingFetchTimeout);
      window.noFilingFetchTimeout = setTimeout(() => {
        const owner = (currentOwnerId && currentOwnerId > 0) ? currentOwnerId : null;
        fetchData(startDate, endDate, owner);
        if (sort === 'Custom') {
          setSort(`${dayjs(startDate).format('MM/DD/YYYY')} - ${dayjs(endDate).format('MM/DD/YYYY')}`);
        }
      }, 300);
    }
    setCalendarShow(false);
  };

  const handleCalendarClose = () => {
    if (!customStart || !customEnd) {
      setSort(prevSortRef.current);
      setCustomStart(prevCustomStartRef.current);
      setCustomEnd(prevCustomEndRef.current);
    }
    setCalendarShow(false);
  };

  useEffect(() => {
    if (customStart && customEnd) {
      const owner = (currentOwnerId && currentOwnerId > 0) ? currentOwnerId : null;
      fetchData(customStart, customEnd, owner);
    }
  }, [customStart, customEnd]);

  const renderLineGraph = useMemo(() => {
    const formattedLabels = formatLabels(data.Labels || []);
    return (
      <LineGraph
        xlabels={formattedLabels}
        label1="Approved For Not Filing"
        label2="Complete"
        dateForLabel1={data.NoFilingData || []}
        dateForLabel2={data.CompleteData || []}
        isMagnified={false}
      />
    );
  }, [data]);

  const OwnerDropdown = (
    <Dropdown
      appearance="outline"
      size="small"
      selectedOptions={[selectedValue]}
      onOptionSelect={handleOwnerChange}
      aria-label="Filter by owner"
      placeholder={currentOwnerName}
      style={{ minWidth: 220 }}
    >
      <Option value="0">All owners</Option>
      {owners.map(o => (
        <Option key={o.OwnerId} value={String(o.OwnerId)}>
          {o.OwnerName}
        </Option>
      ))}
    </Dropdown>
  );

  return (
    <div className="active-strs-doughnut-chart-container">
      <div className="active-str-doughnut-chart-header d-flex justify-content-between align-items-center" style={{ height: "42.5px" }}>
        <div className="active-str-doughnut-header-left d-flex align-items-center" style={{ gap: 8 }}>
          <h4 className="active-str-doughnut-panel-title m-0">
            Approved For Not Filing vs. Completed STRs
          </h4>
          <SortBySelect
            value={sort}
            handleSelect={handleSelect}
            options={['Last 7 Days', 'Last Month', 'Last 3 Months', 'Custom']}
            type={0}
            isMagnified={modalShow}
          />
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <FluentButton
            appearance="none"
            onClick={() => setModalShow(true)}
            icon={<FluentUiIcon name="ArrowMaximizeRegular" color="#0F6CBD" size={20} />}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end px-2">
        {OwnerDropdown}
      </div>

      <Modal
        show={calendarShow}
        onHide={handleCalendarClose}
        className="calendar-modal"
        contentClassName="calendar-modal-content"
        backdropClassName="calendar-modal-backdrop"
        dialogClassName="completed-vs-overdue-modal-dialog"
      >
        <DateRangePicker
          start={customStart}
          setStart={setCustomStart}
          end={customEnd}
          setEnd={setCustomEnd}
        />
      </Modal>

      <div className="mt-2">{renderLineGraph}</div>

      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        dialogClassName="completed-vs-overdue-modal-dialog"
        contentClassName="completed-vs-overdue-modal-styles"
      >
        <Modal.Header className="p-0 w-100">
          <div className="completed-vs-overdue-modal-header-container d-flex w-100 align-items-center justify-content-between" style={{ gap: 8 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <h4 className="active-str-doughnut-panel-title m-0">
                Approved For Not Filing vs. Completed STRs
              </h4>
              <SortBySelect
                value={sort}
                handleSelect={handleSelect}
                options={['Last 7 Days', 'Last Month', 'Last 3 Months', 'Custom']}
                type={0}
                isMagnified={modalShow}
              />
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              {OwnerDropdown}
              <button
                className="completed-vs-overdue-exit-modal header-button"
                onClick={() => setModalShow(false)}
              />
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-0">
          <LineGraph
            xlabels={formatLabels(data.Labels || [])}
            label1="Approved For Not Filing"
            label2="Complete"
            dateForLabel1={data.NoFilingData || []}
            dateForLabel2={data.CompleteData || []}
            isMagnified={true}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

NoFilingVsCompletedLineGraph.propTypes = {
  data: PropTypes.shape({
    Labels: PropTypes.arrayOf(PropTypes.string),
    NoFilingData: PropTypes.arrayOf(PropTypes.number),
    CompleteData: PropTypes.arrayOf(PropTypes.number),
    Owners: PropTypes.arrayOf(PropTypes.shape({
      OwnerId: PropTypes.number.isRequired,
      OwnerName: PropTypes.string.isRequired,
    })),
    CurrentOwner: PropTypes.shape({
      OwnerId: PropTypes.number,
      OwnerName: PropTypes.string,
    }),
  }).isRequired,
  fetchData: PropTypes.func.isRequired,
  customStart: PropTypes.instanceOf(Date),
  customEnd: PropTypes.instanceOf(Date),
  setCustomStart: PropTypes.func.isRequired,
  setCustomEnd: PropTypes.func.isRequired,
  selectedOwnerId: PropTypes.number,
  setSelectedOwnerId: PropTypes.func.isRequired,
};

export default NoFilingVsCompletedLineGraph;